import { useEffect, useState } from "react";
import {
  loadMailNotifications,
  sendMailNotification,
  type MailNotificationRole,
  type MailNotificationStatus,
} from "../../core-api/gmail";
import { Button } from "../../twenty-ui";
const label = { SALES: "销售", SOURCING: "查货" };
export function MailNotificationActions({
  mailboxId,
  messageId,
}: {
  mailboxId: string;
  messageId: string;
}) {
  const [status, setStatus] = useState<MailNotificationStatus | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setError("");
    setStatus(null);
    void loadMailNotifications(mailboxId, messageId)
      .then((value) => {
        if (active) setStatus(value);
      })
      .catch(() => {
        if (active)
          setError("无法读取分发状态，请刷新重试或确认账号具有分发权限。");
      });
    return () => {
      active = false;
    };
  }, [mailboxId, messageId, revision]);
  const send = async (role: MailNotificationRole) => {
    if (!status || busy || status.items.some((item) => item.role === role))
      return;
    if (
      !window.confirm(
        `确认将这封邮件分发给${label[role]}？\n收件人：${status.targets[role]}${status.ccTargets?.[role] ? `\n抄送：${status.ccTargets[role]}` : ""}\n将发送原始主题、正文、安全附件和 CRM 回复入口。这会发送真实邮件。`,
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      const receipt = await sendMailNotification(mailboxId, messageId, role);
      setStatus((current) =>
        current
          ? {
              ...current,
              items: [
                ...current.items.filter((item) => item.role !== role),
                receipt,
              ],
            }
          : current,
      );
    } catch {
      setError("未能确认发送结果。请刷新分发状态，不要反复点击发送。");
      // Lock this role locally until the server's durable status is reloaded.
      setStatus((current) =>
        current
          ? {
              ...current,
              items: [
                ...current.items.filter((item) => item.role !== role),
                {
                  role,
                  state: "UNCONFIRMED",
                  attemptedAt: new Date().toISOString(),
                },
              ],
            }
          : current,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section
      aria-label="邮件分发通知"
      className="mail-notification-panel"
    >
      <h4 className="mail-notification-title">分发通知</h4>
      <p className="mail-notification-description">
        销售通知发送给当前负责人，并抄送销售归档邮箱；安全附件将一并发送。
      </p>
      {error && <p role="alert">{error}</p>}
      {!status && !error && <p role="status">正在读取分发状态…</p>}
      {status && !status.enabled && <p>生产分发暂未开启。</p>}
      {status &&
        (["SALES", "SOURCING"] as const).map((role) => {
          const receipt = status.items.find((item) => item.role === role);
          return (
            <div
              key={role}
              className="mail-notification-target"
            >
              <span className="mail-notification-recipient">
                <strong>{label[role]}通知邮箱</strong>
                <span>{status.targets[role]}</span>
              </span>
              <Button
                variant="primary"
                className="mail-notification-send"
                disabled={busy || !status.enabled || !!receipt}
                onClick={() => void send(role)}
              >
                {busy
                  ? "正在发送…"
                  : receipt?.state === "ACCEPTED"
                    ? "已交给邮件服务"
                    : receipt
                      ? "结果待确认，请勿重发"
                      : `分发给${label[role]}`}
              </Button>
            </div>
          );
        })}
      <Button
        variant="secondary"
        className="mail-notification-refresh"
        disabled={busy}
        onClick={() => setRevision((value) => value + 1)}
      >
        刷新分发状态
      </Button>
      <p className="mail-notification-note">
        “已交给邮件服务”表示 SES
        已接受，不代表收件箱已收到；请以实际收件情况为准。
      </p>
    </section>
  );
}
